import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Menu, X, Ticket, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import LayerSeekLogo from './LayerSeekLogo';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navLinks = [
    { label: 'Experience', href: '#experience' },
    { label: 'Schedule', href: '#schedule' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[var(--bg-primary)]/95 backdrop-blur-md border-b border-[var(--border-subtle)]' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/layerseek" className="flex items-center" data-testid="header-logo">
            <LayerSeekLogo size="default" />
          </a>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              zedit.io
            </Link>
            {navLinks.map((link) => (
              <a 
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>
          
          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <Button 
              asChild
              className="bg-[var(--accent-primary)] text-[var(--bg-primary)] hover:bg-[var(--accent-hover)] h-10 px-6 rounded-xl font-semibold"
            >
              <a
                href="https://luma.com/6kdsrxks"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-get-tickets"
              >
                <Ticket className="w-4 h-4 mr-2" />
                Get Tickets
              </a>
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="md:hidden bg-[var(--bg-secondary)] border-b border-[var(--border-subtle)]"
        >
          <nav className="container mx-auto px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a 
                key={link.label}
                href={link.href}
                className="block text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <Button 
              asChild
              className="w-full bg-[var(--accent-primary)] text-[var(--bg-primary)] hover:bg-[var(--accent-hover)] h-10 rounded-xl font-semibold"
            >
              <a
                href="https://luma.com/6kdsrxks"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-header-get-tickets"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Ticket className="w-4 h-4 mr-2" />
                Get Tickets
              </a>
            </Button>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;